import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { PermissionCheckerService } from 'src/app/services/permission-checker/permission-checker.service';
import { QuoteService } from 'src/app/services/quote/quote.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { finalize } from 'rxjs/operators';
import { ReturnMessage } from 'src/app/models/return-message';
import { ReturnQuoteQuestionsPayload } from 'src/app/models/quote/return-quote-questions-payload';
import { ReturnQuoteQuestion } from 'src/app/models/quote/return-quote-question';
import { QuoteQuestionsNavigationState } from 'src/app/models/quote/quote-questions-navigation-state';
import { Title } from '@angular/platform-browser';
import { AlertController, NavController } from '@ionic/angular';
import { UntypedFormArray } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { PhoneNumberFormat } from 'src/app/models/customer/phone-number-format';
import { PhoneNumberMask } from 'src/app/models/customer/phone-number-mask';
import { ContactAutocompleteDataLoaderService } from 'src/app/services/autocomplete-data-loader/contact-autocomplete-data-loader.service';
import { ContactService } from 'src/app/services/contact/contact.service';
import { AutocompleteOption } from 'src/app/components/autocomplete/autocomplete-option';
import { ResponseContacts } from 'src/app/models/response-contacts';
import { AutocompleteComponent } from 'src/app/components/autocomplete/autocomplete.component';
import { QuoteQuestionDropDownAutocompleteDataLoaderFactoryService } from 'src/app/services/autocomplete-data-loader/quote-question-drop-down-autocomplete-data-loader-factory.service';

@Component({
  selector: 'app-quote-questions',
  templateUrl: './quote-questions.page.html',
  styleUrls: ['./quote-questions.page.scss'],
  providers: [
    { provide: 'instance1', useClass: ContactAutocompleteDataLoaderService },
    { provide: 'instance2', useClass: ContactAutocompleteDataLoaderService },
    { provide: 'instance3', useClass: ContactAutocompleteDataLoaderService }
  ]
})
export class QuoteQuestionsPage implements OnInit {

  title: string = 'Quote Questions';
  quoteQuestions: ReturnQuoteQuestion[] = null;
  disableSubmitButton: boolean;
  canLeave: boolean;
  canCRUDQuotes: boolean;
  form: UntypedFormGroup = new UntypedFormGroup({});
  navigationState: QuoteQuestionsNavigationState;

  maxLengths: any = {
    'Text': 30,
    'Number': 25,
    'Purchase_Order': 15,
    'Email': 40,
    'Default': 255
  };

  DFLT_FIELD_CONF_NAME = 'Default';
  PHONE_NBR_DATA_TYPE = 'PhoneNumber';
  PURCHASE_ORDER_FIELD = 'Purchase_Order';

  CALLER_NAME_FIELD = 'Caller_Name';
  CALLER_PHONE_NUMBER_FIELD = 'Caller_Phone';
  CALLER_EMAIL_ADDRESS_FIELD = 'Caller_Email_Address';
  USER_DEFINED_FIELDS = [this.CALLER_NAME_FIELD, this.CALLER_PHONE_NUMBER_FIELD, this.CALLER_EMAIL_ADDRESS_FIELD];

  userDefinedFieldsMappedToUIControls = [this.PURCHASE_ORDER_FIELD].concat(this.USER_DEFINED_FIELDS);

  fieldsConfiguration: any = {
    'Caller_Name': {
      validationMessages: [],
      validators: [],
      inputMode: 'autocomplete'
    },
    'Caller_Phone': {
      validationMessages: [],
      validators: [],
      inputMode: 'autocomplete'
    },
    'Caller_Email_Address': {
      validationMessages: [],
      validators: [],
      inputMode: 'autocomplete'
    },
    'Text': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Text}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Text)],
      inputMode: 'text'
    },
    'Purchase_Order': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Purchase_Order}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Purchase_Order)],
      inputMode: 'text'
    },
    'Number': {
      validationMessages: [
        { name: 'pattern', message: 'This field only accepts numeric values.' },
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Number}.` }
      ],
      validators: [Validators.pattern(environment.NUMBER_REGEX), Validators.maxLength(this.maxLengths.Number)],
      inputMode: 'number'
    },
    'Email': {
      validationMessages: [
        { name: 'pattern', message: 'This field needs to be a valid email address.' },
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Email}.` }
      ],
      validators: [Validators.pattern(environment.EMAIL_ADDRESS_REGEX), Validators.maxLength(this.maxLengths.Email)],
      inputMode: 'email'
    },
    'DropDown': {
      validationMessages: [],
      validators: [],
      inputMode: 'autocomplete'
    },
    'Default': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Default}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Default)],
      inputMode: 'text'
    }
  };

  phoneNumberMask: PhoneNumberMask;
  maskReady: boolean = false;

  callerNameControl: UntypedFormControl;
  callerPhoneControl: UntypedFormControl;
  callerEmailAddressControl: UntypedFormControl;

  @ViewChild("callerNameAutocomplete") callerNameAutocomplete!: AutocompleteComponent;
  @ViewChild("callerPhoneAutocomplete") callerPhoneAutocomplete!: AutocompleteComponent;
  @ViewChild("callerEmailAutocomplete") callerEmailAutocomplete!: AutocompleteComponent;
  
  get questions() {
    return this.form.get('questions') as UntypedFormArray;
  }

  get shouldRenderContent() {
    return this.quoteQuestions != null && this.questions != null;
  }

  get questionsExist() {
    return this.form && this.quoteQuestions && this.quoteQuestions.length > 0;
  }

  quoteQuestionDropDownDataLoaders: any = {};
  DROPDOWN = "DropDown";

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private navController: NavController,
    private titleService: Title,
    private loadingWrapper: LoadingControllerWrapperService,
    private toastWrapper: ToastControllerWrapperService,
    private permissionChecker: PermissionCheckerService,
    private quoteService: QuoteService,
    private alertCtrl: AlertController,
    @Inject('instance1') public callerNameDataLoader: ContactAutocompleteDataLoaderService, // We need three different instances of the same service
    @Inject('instance2') public callerPhoneDataLoader: ContactAutocompleteDataLoaderService,
    @Inject('instance3') public callerEmailDataLoader: ContactAutocompleteDataLoaderService,
    private quoteQuestionDropDownDataLoaderFactory: QuoteQuestionDropDownAutocompleteDataLoaderFactoryService
  ) { }

  ngOnInit() {
    this.navigationState = <QuoteQuestionsNavigationState>this.router.getCurrentNavigation()?.extras.state;
    this.disableSubmitButton = true;
    this.canLeave = false;
  }

  async ionViewWillEnter() {
    this.titleService.setTitle(this.title);
    if (!this.navigationState) {
      this.navController.navigateRoot(['']);
      return;
    }

    await this.loadingWrapper.presentLoading('');
    forkJoin<[PhoneNumberFormat,boolean,ReturnMessage<ReturnQuoteQuestionsPayload>]>([
      this.route.snapshot.data.phoneNumberFormat,
      this.permissionChecker.canCRUDQuotes(),
      this.quoteService.getQuoteQuestions(this.navigationState.quoteId)
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([
        phoneNumberFormat, 
        canCRUDQuotes, 
        getQuoteQuestions] 
        : [PhoneNumberFormat, 
          boolean, 
          ReturnMessage<ReturnQuoteQuestionsPayload>]) => {

        this.phoneNumberMask = new PhoneNumberMask(phoneNumberFormat.phoneNumberFormat);
        this.maskReady = true;

        this.canCRUDQuotes = canCRUDQuotes;
        this.quoteQuestions = getQuoteQuestions.success ? getQuoteQuestions.payload.questions : [];

        if (!this.quoteQuestions || this.quoteQuestions.length === 0) {
          this.disableSubmitButton = true;
          this.canLeave = true;
        } else {
          this.disableSubmitButton = false;
          this.canLeave = false;
        }

        // Configure the autocomplete data loaders used for caller name, caller phone and caller email
        this.callerNameDataLoader.configuration = {
          customerNumber: getQuoteQuestions.payload.customerNumber,
          addressCode: getQuoteQuestions.payload.addressCode,
          searchField: ContactService.CONTACT_NAME
        };
        this.callerPhoneDataLoader.configuration = {
          customerNumber: getQuoteQuestions.payload.customerNumber,
          addressCode: getQuoteQuestions.payload.addressCode,
          searchField: ContactService.PHONE_NUMBER
        };
        this.callerEmailDataLoader.configuration = {
          customerNumber: getQuoteQuestions.payload.customerNumber,
          addressCode: getQuoteQuestions.payload.addressCode,
          searchField: ContactService.EMAIL
        };

        // The phone number autocomplete needs a function to apply the phone number format
        // but we need to pass the format configuration, so instead of passing just a function
        // we pass in an object declaring the format function and also a config object
        // that allows to get access to the format configuration.
        let self = this;
        this.getSelectedContactPhoneNumber = {
          config: self,
          format: function(option: AutocompleteOption) {
            return option 
              ? this.config.phoneNumberMask.formatValue(option.sourceObject.phoneNumber)
              : '';
          }
        };

        this.buildForm();
      },
      error => {
        // the api returns HTTP 400 when there is no questions instead of an empty list...
        this.quoteQuestions = [];
      });
  }

  // This object if used by the contact phone number autocomplete to format the phone number selected
  getSelectedContactPhoneNumber: { config: any, format: (args: AutocompleteOption) => string };

  // This function is used by the contact email autocomplete to format the value selected
  getSelectedContactEmail(option: AutocompleteOption) { return option ? option.sourceObject.email : ''; }

  buildForm() {
    this.form = this.fb.group({
      questions: this.fb.array([])
    });

    let contact = this.getContactFromQuestionsList();

    for (const question of this.quoteQuestions) {
      let fieldConfiguration = this.getFieldConfigurationByQuestion(question);
      if (!fieldConfiguration)
        continue;

      let initialValue = null;

      if (question.userDefinedField === this.CALLER_NAME_FIELD && contact != null && contact.name)
      {
        initialValue = { id: contact.contactId, description: contact.name, sourceObject: contact } as AutocompleteOption;
      }
      else if (question.userDefinedField === this.CALLER_PHONE_NUMBER_FIELD && contact != null && contact.phoneNumber)
      {
        initialValue = { id: contact.contactId, description: contact.phoneNumber, sourceObject: contact } as AutocompleteOption;
      }
      else if (question.userDefinedField === this.CALLER_EMAIL_ADDRESS_FIELD && contact != null && contact.email)
      {
        initialValue = { id: contact.contactId, description: contact.email, sourceObject: contact } as AutocompleteOption;
      }
      else if (question.dataType === this.DROPDOWN)
      {
        if (question.value)
        {
          initialValue = { id: question.value, description: question.value, sourceObject: {value: question.value, label: question.value} } as AutocompleteOption;
        }
      }
      else 
      {
        initialValue = this.getFormControlInitialValue(question);
      }

      let control = this.fb.control(initialValue, fieldConfiguration.validators);

      if (question.userDefinedField === this.CALLER_NAME_FIELD && question.dataType !== this.DROPDOWN) // keep a reference for later use
      {
        this.callerNameControl = control;
      }
      if (question.userDefinedField === this.CALLER_PHONE_NUMBER_FIELD && question.dataType !== this.DROPDOWN) // keep a reference for later use
      {
        this.callerPhoneControl = control;
      }
      if (question.userDefinedField === this.CALLER_EMAIL_ADDRESS_FIELD && question.dataType !== this.DROPDOWN) // keep a reference for later use
      {
        this.callerEmailAddressControl = control;
      }

      if (question.dataType === this.DROPDOWN)
      {
        this.quoteQuestionDropDownDataLoaders[question.id] = this.quoteQuestionDropDownDataLoaderFactory.createInstance(question.dropDownId);
      }

      this.questions.push(control);
    }
  }

  getContactFromQuestionsList()
  {
    let name = null;
    let phoneNumber = null;
    let emailAddress = null;
    let contact = null;

    for (const question of this.quoteQuestions) {
      if (question.value)
      {
        if (question.userDefinedField === this.CALLER_NAME_FIELD)
        {
          name = question.value;
        }

        if (question.userDefinedField === this.CALLER_EMAIL_ADDRESS_FIELD)
        {
          emailAddress = question.value;
        }
        
        if (question.userDefinedField === this.CALLER_PHONE_NUMBER_FIELD)
        {
          phoneNumber = question.value;
        }
      }
    }

    if (name != null || phoneNumber != null || emailAddress != null)
    {
      name = name || '';
      emailAddress = emailAddress || '';
      phoneNumber = phoneNumber || '';
      contact = { 
        contactId: '-1', 
        name: name, 
        email: emailAddress, 
        phoneNumber: phoneNumber
      } as ResponseContacts;
    }

    return contact;
  }

  getFormControlInitialValue(question: ReturnQuoteQuestion) {
    let questionInitialValue = question.value || '';

    return questionInitialValue;
  }

  getFieldConfigurationByQuestion(question: ReturnQuoteQuestion) {
    // If no question object was passed in then return the default configuration
    let dfltConf = this.fieldsConfiguration[this.DFLT_FIELD_CONF_NAME];
    if (!question)
      return dfltConf;

    // Check if the question needs to be mapped to a UI control based on the userDefinedField or the dataType

    if (this.userDefinedFieldsMappedToUIControls.indexOf(question.userDefinedField) > 0)
    {
      // Return the form field configuration based on the userDefinedField value
      return this.fieldsConfiguration[question.userDefinedField] || dfltConf;
    }

    // Return the form field configuration based on the dataType
    return this.fieldsConfiguration[question.dataType] || dfltConf;
  }

  getFieldConfigurationByQuestionIndex(index: number) {
    return this.getFieldConfigurationByQuestion(this.quoteQuestions[index]);
  }

  getQuestionDescription(index: number) {
    return this.quoteQuestions[index] ? this.quoteQuestions[index].question : '';
  }

  getFormControlType(index: number) {
    // If no question found return Default type
    let question = this.quoteQuestions[index];
    if (!question)
    {
      return this.DFLT_FIELD_CONF_NAME;
    }

    // Check if the question needs to be mapped to a UI control based on the userDefinedField or the dataType
    if (this.userDefinedFieldsMappedToUIControls.indexOf(question.userDefinedField) > 0 && question.dataType !== 'DropDown')
    {
      return question.userDefinedField;
    }

    // Return the question data type
    return question.dataType || this.DFLT_FIELD_CONF_NAME;
  }

  getValidationMessages(index: number) {
    let fieldConfiguration = this.getFieldConfigurationByQuestionIndex(index);
    return fieldConfiguration.validationMessages;
  }

  getInputMode(index: number) {
    let fieldConfiguration = this.getFieldConfigurationByQuestionIndex(index);
    return fieldConfiguration.inputMode;
  }

  updatePhoneNumberFormat(questionIndex: number) {
    if (this.quoteQuestions[questionIndex].userDefinedField == this.CALLER_PHONE_NUMBER_FIELD) {
      // If the field is caller phone number we need to send back the UNFORMATTED phone number
      let unformattedPhoneNumber = this.phoneNumberMask.formatValue(this.form.value.questions[questionIndex]);
      return unformattedPhoneNumber;
    }
    else {
      // for user defined fields with phone data type we need to send the FORMATTED phone number
      console.log(this.form.value.questions[questionIndex]);
      let formattedPhoneNumber = this.phoneNumberMask.formatValue(this.form.value.questions[questionIndex], true);
      return formattedPhoneNumber;
    }
  }

  async submit() {

    if (this.form.valid) {
      this.canLeave = true;

      await this.toastWrapper.dismissToast();
      await this.loadingWrapper.presentLoading('');

      let post = { questionsAnswers: [] };
      let validCallerEmailAddress: boolean = true;

      for (var i = 0; i < this.quoteQuestions.length; i++) {

        let question = this.quoteQuestions[i];
        let formControlValue = this.form.value.questions[i];
        let questionValue = '';

        if (question.userDefinedField == this.CALLER_NAME_FIELD && question.dataType !== this.DROPDOWN)
        {
          if (formControlValue)
          {
            // retrieve the value from the selected option in the autocomplete
            questionValue = formControlValue.sourceObject ? formControlValue.sourceObject.name : '';
          }
          else
          {
            // when no selected option exist in the autocomplete retrieve the text the user entered
            questionValue = this.callerNameAutocomplete.getSearchTerm() || '';
          }
        }
        else if (question.userDefinedField == this.CALLER_PHONE_NUMBER_FIELD && question.dataType !== this.DROPDOWN)
        {
          if (formControlValue)
          {
            // retrieve the value from the selected option in the autocomplete
            questionValue = formControlValue.sourceObject ? formControlValue.sourceObject.phoneNumber : '';
          }
          else
          {
            // when no selected option exist in the autocomplete retrieve the text the user entered
            questionValue = this.callerPhoneAutocomplete.getSearchTerm() || '';

            // remove the phone format
            questionValue = this.phoneNumberMask.formatValue(questionValue);

            // if the phone number is all zeros then no phone number is saved
            if (this.phoneNumberMask.isEmpty(questionValue))
            {
              questionValue = '';
            }
          }
        }
        else if (question.userDefinedField == this.CALLER_EMAIL_ADDRESS_FIELD && question.dataType !== this.DROPDOWN)
        {
          if (formControlValue)
          {
            // retrieve the value from the selected option in the autocomplete
            questionValue = formControlValue.sourceObject ? formControlValue.sourceObject.email : '';
          }
          else
          {
            // when no selected option exist in the autocomplete retrieve the text the user entered
            questionValue = this.callerEmailAutocomplete.getSearchTerm() || '';
          }
          
          if (questionValue !== '')
          {
            // verify if the text is a valid email address
            // Using this validation logic because the current autocomplete component cannot handle email validation.
            validCallerEmailAddress = (new RegExp(environment.EMAIL_ADDRESS_REGEX).test(questionValue));
            if (!validCallerEmailAddress)
            {
              await this.loadingWrapper.dismissLoading();
              const alert = await this.alertCtrl.create({
                header: 'Invalid email address',
                message: "Enter a valid caller email address.",
                buttons: [{ text: 'OK' }],
              });
              await alert.present();
            }
          }
        }
        else if (question.dataType == this.PHONE_NBR_DATA_TYPE && question.dataType !== this.DROPDOWN) 
        {
          // update the phone number format for regular fields (fields not mapped to user defined fields)
          questionValue = this.updatePhoneNumberFormat(i); 
        }
        else if (question.dataType == this.DROPDOWN) {
          if (formControlValue) {
            questionValue = formControlValue.sourceObject.value || formControlValue.description;
          }
        }
        else
        {
          // regular form control value
          questionValue = formControlValue;
        }

        // add the question answer to the post request payload
        post.questionsAnswers.push({ id: question.id, value: questionValue });

      } // end of the quoteQuestions for loop

      if (validCallerEmailAddress) // check if the caller email address is valid
      {
        this.quoteService.updateQuoteQuestions(post)
        .pipe(finalize(async () => { await this.loadingWrapper.dismissLoading(); }))
        .subscribe((response) => {
          if (response.success) {
            this.router.navigate(['..'], { relativeTo: this.route });
          }
        });
      }
    } else {
      await this.toastWrapper.dismissToast();
      await this.toastWrapper.presentErrorToast('Please complete the form');
    }
  }

  // This variable is used by the event handler functions: onContactByNameSelected, onContactByPhoneSelected and onContactByEmailSelected
  // to avoid one event handler interfering with another event handler.
  updatingCallerFields: boolean = false;

  // When a contact is selected using the contact name autocomplete
  // we need to update the other autocompletes with the corresponding 
  // values from the contact object.
  onContactByNameSelected(contact: AutocompleteOption)
  {
    // If the user cleared the contact name autocomplete selection then we need to 
    // clear the selection of the other autocompletes.

    // Do not process the event if it was triggered by another autocomplete.
    if (this.updatingCallerFields)
      return;

    this.updatingCallerFields = true;

    if (this.callerEmailAddressControl && (contact == null || (contact && contact.sourceObject)))
    {
      let patchValue = (contact != null && contact.sourceObject.email) ? contact : null;
      this.callerEmailAddressControl.patchValue(patchValue, { emitEvent: false });
    }

    if(this.callerPhoneControl && (contact == null || (contact && contact.sourceObject)))
    {
      let patchValue = (contact != null && contact.sourceObject.phoneNumber) ? contact : null;
      this.callerPhoneControl.patchValue(patchValue, { emitEvent: false });
    }
    
    this.updatingCallerFields = false;
  }

  // When a contact is selected using the contact phone number autocomplete
  // we need to update the other autocompletes with the corresponding 
  // values from the contact object.
  onContactByPhoneSelected(contact: AutocompleteOption)
  {
    // If the user cleared the selection then do not affect the other autocompletes.
    // Do not process the event if it was triggered by another autocomplete.
    if (!contact || this.updatingCallerFields) 
      return;

    this.updatingCallerFields = true; // this flag prevents the handlers of the other contact autocompletes from running

    // If the contact selected do not have phone number then clear the value of the phone number autocomplete
    if (this.callerPhoneControl && contact.sourceObject.phoneNumber == '')
    {
      this.callerPhoneControl.patchValue(null, { emitEvent: false});
    }

    // Update the contact name autocomplete
    if (this.callerNameControl && contact.sourceObject)
    {
      let patchValue = (contact.sourceObject.name) ? contact : null;
      this.callerNameControl.patchValue(patchValue, { emitEvent: false });
    }

    // Update the contact email address autocomplete
    if(this.callerEmailAddressControl && contact.sourceObject)
    {
      let patchValue = (contact.sourceObject.email) ? contact : null;
      this.callerEmailAddressControl.patchValue(patchValue, { emitEvent: false });
    }
    
    this.updatingCallerFields = false; // reset handlers flag
  }

  // When a contact is selected using the contact email autocomplete
  // we need to update the other autocompletes with the corresponding 
  // values from the contact object.
  onContactByEmailSelected(contact: AutocompleteOption)
  {
    // If the user cleared the contact by email autocomplete selection then 
    // do not affect the other autocompletes. This is to mimic GPs behavior.
    // Do not process the event if it was triggered by another autocomplete.
    if (!contact || this.updatingCallerFields)
      return;

    this.updatingCallerFields = true; // this flag prevents the handlers of the other contact autocompletes from running

    // If the contact selected do not have an email then clear the value of the email autocomplete.
    if (this.callerEmailAddressControl && contact.sourceObject.email == '')
    {
      this.callerEmailAddressControl.patchValue(null, { emitEvent: false});
    }

    // Update the contact name autocomplete.
    if (this.callerNameControl && contact.sourceObject)
    {
      // If name is empty string then clear the autocomplete value object.
      // All contacts should have a name but check anyway.
      let patchValue = (contact.sourceObject.name) ? contact : null;
      this.callerNameControl.patchValue(patchValue, { emitEvent: false });
    }

    // Update the contact phone number autocomplete.
    if(this.callerPhoneControl && contact.sourceObject)
    {
      // If phone number is empty string then clear the autocomplete value object.
      let patchValue = (contact.sourceObject.phoneNumber) ? contact : null;
      this.callerPhoneControl.patchValue(patchValue, { emitEvent: false });
    }
    
    this.updatingCallerFields = false; // reset handlers flag
  }

  getDropDownDataLoader(index: number) {
    return this.quoteQuestionDropDownDataLoaders[this.quoteQuestions[index].id];
  }
}
